import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import ErrorBoundary from 'components/error-boundary';
import SuperModal from 'components/modals';
import Surface from 'components/Surface';
import { DateTime } from 'luxon';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { clearHourDraft } from 'redux/modules/hours.actions';
import { clearHourHistory } from 'redux/modules/hours.types';
import type { GetHourWithHourEditorModalProps } from 'redux/selectors/hours.draft';
import { getHourWithHourEditorModalProps } from 'redux/selectors/hours.draft';
import type { IState } from 'redux/state';
import type { MappedActions } from 'types/redux';
import HourDraftPreview from './HourDraftPreview';
import HourEditor from './HourEditor';
import styles from './HourWithHourEditorModal.module.scss';
import MemorySidebar from './MemorySidebar';
import TicMemorySidebar from './tic/timeline/TicMemorySidebar';

interface ExternalProps {
  popoverPosition?: Placement;
  sizeBasedOnDuration?: boolean;
  children?: React.ReactElement;
}

const mapStateToProps = (state: IState) => getHourWithHourEditorModalProps(state);
const mapActionsToProps = { clearHourDraft, clearHourHistory };

type ActionProps = MappedActions<typeof mapActionsToProps>;

type Props = ExternalProps & GetHourWithHourEditorModalProps & ActionProps;

export class HourWithHourEditorModal extends PureComponent<Props> {
  static defaultProps = {
    popoverPosition: 'right-start' as Placement,
  };

  handleModalClose = () => {
    this.props.clearHourDraft();
    this.props.clearHourHistory();
  };

  renderModalContent = () => {
    const { draft, ui } = this.props;

    const editorContainerClass = classNames(styles.container, {
      [styles.showMemory]: ui.showMemory,
      [styles.hasEnabledTic]: ui.hasEnabledTic,
    });

    return (
      <div className={editorContainerClass}>
        <Surface surface={1} className={styles.hourEditorColumn}>
          {ui.isActingAsOtherUser && <div className={styles.dateLabel}>{this.getDateLabel()}</div>}
          <div className={styles.hourEditor}>
            <HourEditor />
          </div>
        </Surface>
        {ui.showMemory && draft.day && (
          <Surface className={styles.memorySidebar}>
            {ui.hasEnabledTic ? (
              <TicMemorySidebar date={draft.day} />
            ) : (
              <MemorySidebar compact date={draft.day} />
            )}
          </Surface>
        )}
      </div>
    );
  };

  getDateLabel() {
    const day = this.props.draft.day;
    if (!day) {
      return null;
    }
    const now = DateTime.now();
    const draftDate = DateTime.fromISO(day);
    const isToday = +draftDate.startOf('day') === +now.startOf('day');
    const shouldAppendYear = !draftDate.hasSame(now, 'year');
    const format = `${isToday ? `'Today'` : 'EEEE'}, ${
      shouldAppendYear ? 'MMM d, yyyy' : 'MMMM d'
    }`;
    const dateLabel = draftDate.toFormat(format);
    return dateLabel;
  }

  render() {
    const { ui, disableTrap, sizeBasedOnDuration } = this.props;
    const wrapperClass = classNames(styles.modalContainer, {
      [styles.showMemory]: ui.showMemory,
      [styles.hasEnabledTic]: ui.hasEnabledTic,
    });
    const children = this.props.children || (
      <HourDraftPreview sizeBasedOnDuration={sizeBasedOnDuration} />
    );

    return (
      <ErrorBoundary>
        {/* We're using a custom modal implementation here, as the <Modal /> core is rotten
        and doesn't deal well with content that changes height/width after opening.*/}

        {/** @todo Get on Radix `<Modal />` train here already. Parent comment is 6 years old 🤦‍♂️  */}
        <React.Fragment>
          <SuperModal
            style={{
              content: {
                backgroundColor: 'transparent',
                boxShadow: 'initial',
                border: 'initial',
              },
            }}
            allowOverlayNavbar
            shouldCloseOnOverlayClick={!disableTrap}
            shouldCloseOnEsc={!disableTrap}
            isOpen
            onClose={this.handleModalClose}
          >
            <div className={wrapperClass}>{this.renderModalContent()}</div>
          </SuperModal>
          {children}
        </React.Fragment>
      </ErrorBoundary>
    );
  }
}

export default connect(mapStateToProps, mapActionsToProps)(HourWithHourEditorModal);
